/**
 * Página de Audiências (overhaul v4 — Fase 4).
 *
 * Agenda cronológica das audiências judiciais. Reúne:
 *  - prazos marcados como tipo_evento='audiencia' (data/hora do evento);
 *  - data/hora extraídas pela triagem Haiku das publicações.
 *
 * Todos os usuários ativos visualizam. O agendamento/ajuste manual de
 * data/hora (quando o Haiku não capturou) é restrito a admin.
 */
import { useState } from 'react';
import { CriarAudienciaDialog } from '../components/CriarAudienciaDialog';
import { PageShell } from '../components/PageShell';
import { ehAdmin, useAgendarAudiencia, useAudiencias, useMe } from '../hooks';
import type { AudienciaItem } from '../types';
import { fmtData } from '../utils';

function chave(a: AudienciaItem): string {
  // Agrupa por dia (ou "sem data" no topo, para agendar)
  return a.data_audiencia ?? '0000-00-00';
}

export function Audiencias() {
  const { data: me } = useMe();
  const admin = ehAdmin(me?.papel);
  // Ajuste de data/hora da audiência: admin, gerente E colaborador.
  const podeAjustar = admin || me?.papel === 'gerente' || me?.papel === 'colaborador';
  const { data: audiencias, isLoading } = useAudiencias();
  const agendar = useAgendarAudiencia();
  const [erro, setErro] = useState<string | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [editData, setEditData] = useState('');
  const [editHora, setEditHora] = useState('09:00');
  const [criarOpen, setCriarOpen] = useState(false);
  const [busca, setBusca] = useState('');

  async function salvar(id: number) {
    if (!editData) return;
    setErro(null);
    try {
      await agendar.mutateAsync({ id, body: { data_audiencia: editData, hora_audiencia: editHora || '09:00' } });
      setEditId(null);
    } catch (err: any) {
      if (err.status === 403) setErro('Sem permissão para ajustar a audiência.');
      else setErro(err?.message ?? 'Erro ao agendar audiência');
    }
  }

  function abrirEdicao(a: AudienciaItem) {
    setEditId(a.id_prazo);
    setEditData(a.data_audiencia ?? '');
    setEditHora(a.hora_audiencia ?? '09:00');
  }

  // Filtro por número do processo (no contexto da lista de audiências, que já
  // vem completa do backend — compara só dígitos, com ou sem máscara).
  const buscaDigitos = busca.replace(/\D/g, '');
  const lista = (audiencias ?? []).filter(
    (a) => !buscaDigitos || (a.numero_processo || '').replace(/\D/g, '').includes(buscaDigitos),
  );
  // Já vem ordenado do backend (sem data primeiro, depois por data/hora).
  const semData = lista.filter((a) => !a.data_audiencia);
  const comData = lista.filter((a) => a.data_audiencia);

  // Agrupa as com data por dia
  const grupos: { dia: string; itens: AudienciaItem[] }[] = [];
  for (const a of comData) {
    const k = chave(a);
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.dia === k) ultimo.itens.push(a);
    else grupos.push({ dia: k, itens: [a] });
  }

  const inputClasse =
    'text-sm border border-gray-300 dark:border-gray-600 rounded p-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500';

  function linhaAudiencia(a: AudienciaItem) {
    const emEdicao = editId === a.id_prazo;
    return (
      <div
        key={a.id_prazo}
        className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-white/10 dark:border-white/5 hover:bg-white/5 dark:hover:bg-slate-900/5 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-800 dark:text-gray-100 truncate">
            {a.numero_processo}
            <span className="ml-2 text-[10px] text-gray-400">{a.sigla_tribunal ?? ''}</span>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {a.tipo_prazo}
            <span className="ml-1 text-gray-400">
              · {a.origem === 'triagem' ? '🤖 triagem Haiku' : '📅 evento'}
            </span>
          </div>
          {a.teor_resumo && (
            <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 line-clamp-2">{a.teor_resumo}</div>
          )}
        </div>

        <div className="text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
          {a.hora_audiencia ? `🕐 ${a.hora_audiencia}` : <span className="text-gray-400">sem hora</span>}
        </div>

        {podeAjustar && (
          emEdicao ? (
            <div className="flex items-center gap-1.5">
              <input type="date" value={editData} onChange={(e) => setEditData(e.target.value)} className={inputClasse} />
              <input type="time" value={editHora} onChange={(e) => setEditHora(e.target.value)} className={inputClasse} />
              <button
                onClick={() => salvar(a.id_prazo)}
                disabled={agendar.isPending || !editData}
                className="px-2.5 py-1.5 text-xs btn-glass-neuro-primary disabled:opacity-50 rounded font-semibold"
              >
                {agendar.isPending ? '⏳' : 'Salvar'}
              </button>
              <button onClick={() => setEditId(null)} className="px-2.5 py-1.5 text-xs btn-glass-neuro rounded font-medium">
                ✕
              </button>
            </div>
          ) : (
            <button
              onClick={() => abrirEdicao(a)}
              className="px-2.5 py-1 text-xs btn-glass-neuro rounded font-semibold"
            >
              {a.data_audiencia ? '✏️ Ajustar' : '📅 Agendar'}
            </button>
          )
        )}
      </div>
    );
  }

  return (
    <PageShell
      titulo={<h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">⚖️ Audiências</h1>}
      acoes={
        <div className="flex items-center gap-2">
          <div className="relative">
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar processo…"
              title="Filtra as audiências pelo número do processo (com ou sem máscara)"
              className="w-36 sm:w-48 pl-7 pr-2 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">🔍</span>
          </div>
          <button
            onClick={() => setCriarOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm btn-glass-neuro-primary rounded-lg font-semibold shadow-sm"
            title="Incluir manualmente uma audiência que não está no cadastro"
          >
            <span className="text-base leading-none">+</span> Nova audiência
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {erro && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded border border-red-200 dark:border-red-800">
            ⚠ {erro}
          </div>
        )}

        {isLoading && <div className="text-sm text-gray-400">carregando…</div>}

        {!isLoading && lista.length === 0 && (
          <div className="text-sm text-gray-400 glass-panel rounded-xl p-6 text-center shadow-lg">
            Nenhuma audiência identificada. A triagem do Haiku marca data/hora automaticamente;
            o admin pode agendar manualmente quando necessário.
          </div>
        )}

        {/* Sem data — precisam de agendamento manual */}
        {semData.length > 0 && (
          <section className="glass-panel rounded-xl shadow-lg border border-amber-500/20 overflow-hidden">
            <header className="px-4 py-2 bg-amber-500/10 text-amber-600 dark:text-amber-300 text-xs font-bold uppercase tracking-wide border-b border-amber-500/15">
              ⏳ Sem data definida — agendar
            </header>
            {semData.map(linhaAudiencia)}
          </section>
        )}

        {/* Com data — agrupadas por dia */}
        {grupos.map((g) => (
          <section key={g.dia} className="glass-panel rounded-xl shadow-lg border border-white/10 dark:border-white/5 overflow-hidden">
            <header className="px-4 py-2 bg-white/10 dark:bg-slate-900/30 text-gray-700 dark:text-gray-200 text-sm font-semibold border-b border-white/10 dark:border-white/5">
              📅 {fmtData(g.dia)}
            </header>
            {g.itens.map(linhaAudiencia)}
          </section>
        ))}
      </div>

      {criarOpen && <CriarAudienciaDialog onClose={() => setCriarOpen(false)} />}
    </PageShell>
  );
}
